import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { InternalStandardProps } from "@mui/material";
import { Layout } from "../Layout";
import { useNavigate } from "react-router-dom";
import {
    useGlobalGifts,
    useProspectingUser,
    useReceivedMemberVideoGifts,
    useReceivedProspectVideoGifts,
} from "JS/React/Hooks/Gifts";
import { AppHeader } from "JS/React/Components/AppHeader";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { useRouting } from "JS/React/Hooks/Routes";
import { useGetFavorites, useGlobalFavorites } from "JS/React/Hooks/Favorites";
import { useGlobalVideos, useVideos } from "JS/React/Hooks/Video";
import { uniqBy } from "lodash-es";
import { messaging } from "JS/Helpers/UserMessaging";
import { FavoritesTypes } from ".";
import {
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
    Content,
} from "JS/Models";
import { ListingComponent } from "../Audios/ListingComponent";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";

const StyledRoot = styled("div")({});

export interface FavoritesVideosListingProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const FavoritesVideosListing = (props: FavoritesVideosListingProps) => {
    const { className, ...rest } = props;
    const {
        homeStack,
        favoritesVideosStack,
        resetFavoritesVideosStack,
        setGlobalFavoritesVideosStack,
    } = useGlobalNavStack();
    const [searchQuery, setSearchQuery] = useState<string>(
        favoritesVideosStack?.searchQuery || "",
    );
    const [headerTitle, setHeaderTitle] = useState("Favorite Videos");
    const navigate = useNavigate();

    const { videos, resumeUserVideos } = useGlobalVideos();
    const { loading: videosLoading } = useVideos();
    const { isProsUser, loading: loadingProsStatus } = useProspectingUser();

    const { linkProvider } = useRouting();

    const { receivedMemberVideos, receivedProspectVideos } = useGlobalGifts();

    const { loading: receivedMemberVideoGiftsLoading } =
        useReceivedMemberVideoGifts(
            !!receivedMemberVideos && receivedMemberVideos.length > 0,
        );

    const { loading: receivedProspectVideoGiftsLoading } =
        useReceivedProspectVideoGifts(
            !isProsUser || receivedProspectVideos?.length > 0,
        );

    const { favNids } = useGlobalFavorites();
    const { loading: favLoading } = useGetFavorites(!!favNids?.length);

    const filteredVideos = useMemo(() => {
        return uniqBy(
            [...videos, ...receivedMemberVideos, ...receivedProspectVideos],
            (x) => x.nid,
        );
    }, [videos, receivedMemberVideos, receivedProspectVideos]);

    const filterFavoritesVideos = useMemo(
        () => filteredVideos.filter((video) => favNids?.includes(video.nid)),
        [filteredVideos, favNids],
    );

    const searchResults = useMemo(() => {
        return filterFavoritesVideos?.filter(
            (d) =>
                d?.title
                    ?.toLocaleLowerCase()
                    ?.includes(searchQuery?.toLocaleLowerCase().trim()) ||
                d?.description
                    ?.toLocaleLowerCase()
                    ?.includes(searchQuery?.toLocaleLowerCase().trim()),
        );
    }, [filterFavoritesVideos, searchQuery]);

    useEffect(() => {
        setGlobalFavoritesVideosStack({
            searchQuery: searchQuery,
        });
    }, [searchResults]);

    const isVideosContentLoaded =
        !videosLoading &&
        !receivedMemberVideoGiftsLoading &&
        !receivedProspectVideoGiftsLoading &&
        !loadingProsStatus;

    useEffect(() => {
        window.scrollTo(0, 500);
        window.scrollTo(0, 0);
    }, []);

    const isResumeable = (filteredVideo: Content) => {
        if (
            resumeUserVideos &&
            resumeUserVideos.filter((x) => x.nid === filteredVideo.nid).length >
                0
        ) {
            const resumeData = resumeUserVideos.find(
                (x) => x.nid === filteredVideo.nid,
            );
            return !!resumeData;
        } else {
            return false;
        }
    };
    const { handleListItemClick } = useHandlers(
        searchQuery,
        FavoritesTypes.FAVVIDEOS,
    );
    return (
        <Layout>
            <StyledRoot className={className} {...rest}>
                <AppHeader
                    title={headerTitle || "Videos"}
                    canGoBack
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={"Search Videos by title/description"}
                    onBackClick={() => {
                        setSearchQuery("");
                        resetFavoritesVideosStack();
                        if (!homeStack?.isHomeTrack) {
                            navigate(
                                linkProvider.react.favorites().index(),
                            );
                        } else {
                            navigate(linkProvider.react.home());
                        }
                    }}
                />
                {isVideosContentLoaded && !favLoading ? (
                    <ListingComponent
                        listContent={searchResults as Content[]}
                        searchQuery={searchQuery}
                        categoryId={FavoritesTypes.FAVVIDEOS}
                        showSortHeader={true}
                        setGlobalStack={setGlobalFavoritesVideosStack}
                        globalStack={favoritesVideosStack}
                        isResumeable={isResumeable}
                        favoritesContent={favNids}
                        handleListItemClick={handleListItemClick}
                        controlType="videoFavorite"
                    />
                ) : (
                    <AppBackdropProgress
                        open={true}
                        backdropText={
                            favLoading
                                ? messaging.loader.favorites
                                : messaging.loader.audios
                        }
                    />
                )}
            </StyledRoot>
        </Layout>
    );
};
const useHandlers = (searchQuery: string, categoryId: string) => {
    const { logFirebaseEvent } = useFirebaseLogger();
    const { linkProvider } = useRouting();
    const navigate = useNavigate();
    const handleListItemClick = (
        filteredVideo: Content,
        isAutoPlay: string,
        scrollIndex: number,
    ) => {
        if (searchQuery) {
            const action: FirebaseEventAction = {
                action: EventActions.SEARCH,
                category: filteredVideo.isReceived
                    ? EventCategories.GIFT_VIDEOS
                    : EventCategories.VIDEOS,
                skuId: filteredVideo.sku_id,
                nId: filteredVideo.nid,
                contentTitle: filteredVideo.title,
                searchQuery: searchQuery,
            };

            logFirebaseEvent(
                filteredVideo.isReceived
                    ? EventNames.GIFT_VIDEO_SEARCH_OPENED
                    : EventNames.VIDEO_SEARCH_VIDEO_OPENED,
                action,
            );
        }
        navigate(
            linkProvider.react
                .favorites()
                .favoriteVideosDetail(
                    filteredVideo.nid,
                    categoryId,
                    "true",
                    isAutoPlay,
                    {
                        scrollTo: scrollIndex ? +scrollIndex : 0,
                    },
                ),
        );
    };

    return { handleListItemClick };
};
